import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EQUIPMENT = ["Dry Van", "Reefer", "Flatbed", "Power Only", "Box Truck", "Other"];

const inputCls =
  "w-full bg-transparent border border-white/20 px-4 py-3.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-white transition-colors duration-300";

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
      {label}
    </label>
    {children}
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", mc_number: "",
    phone: "", email: "", equipment: "Dry Van", trucks: "", message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        mc_number: form.mc_number || null,
        trucks: form.trucks ? Number(form.trucks) : null,
        message: form.message || null,
      });
      toast.success(`Thank you, ${form.first_name}. Your carrier interest form has been received.`);
      setForm({ first_name: "", last_name: "", company: "", mc_number: "", phone: "", email: "", equipment: "Dry Van", trucks: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-28 sm:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
            Let's Work — Get Dispatched
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-none text-3xl sm:text-5xl lg:text-6xl mb-8">
            Ready to get started?
          </h2>
          <p className="text-neutral-400 leading-relaxed max-w-md">
            Complete the carrier interest form and we'll begin the onboarding conversation.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="border border-white/15 bg-[#101010] p-6 sm:p-10 space-y-6"
            data-testid="carrier-interest-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="First Name *" id="first_name">
                <input id="first_name" data-testid="form-first-name-input" required value={form.first_name} onChange={set("first_name")} className={inputCls} />
              </Field>
              <Field label="Last Name *" id="last_name">
                <input id="last_name" data-testid="form-last-name-input" required value={form.last_name} onChange={set("last_name")} className={inputCls} />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Company Name *" id="company">
                <input id="company" data-testid="form-company-input" required value={form.company} onChange={set("company")} className={inputCls} />
              </Field>
              <Field label="MC / DOT Number" id="mc_number">
                <input id="mc_number" data-testid="form-mc-number-input" value={form.mc_number} onChange={set("mc_number")} className={inputCls} placeholder="MC-123456" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Phone *" id="phone">
                <input id="phone" data-testid="form-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} />
              </Field>
              <Field label="Email *" id="email">
                <input id="email" data-testid="form-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Equipment Type *" id="equipment">
                <select id="equipment" data-testid="form-equipment-select" value={form.equipment} onChange={set("equipment")} className={`${inputCls} bg-[#101010]`}>
                  {EQUIPMENT.map((eq) => (
                    <option key={eq} value={eq}>{eq}</option>
                  ))}
                </select>
              </Field>
              <Field label="Trucks Operated" id="trucks">
                <input id="trucks" data-testid="form-trucks-input" type="number" min="1" value={form.trucks} onChange={set("trucks")} className={inputCls} />
              </Field>
            </div>

            <Field label="Preferred Lanes / Dispatch Needs" id="message">
              <textarea
                id="message"
                data-testid="form-message-textarea"
                rows={5}
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-y`}
                placeholder="Example: I run dry van and prefer Southeast regional freight..."
              />
            </Field>

            <button
              type="submit"
              data-testid="carrier-form-submit-button"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-3 bg-white text-black font-mono text-xs uppercase tracking-[0.25em] px-8 py-5 hover:bg-neutral-300 disabled:opacity-50 transition-colors duration-300"
            >
              {sending ? "Submitting..." : "Submit Carrier Interest"}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
